import {
  Component,
  HostBinding,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Location } from '@freelancer/location';
import { SpinnerColor, SpinnerSize } from '@freelancer/ui/spinner';
import { isLinkWhitelisted } from '../helpers/helpers';

export enum ButtonColor {
  SECONDARY = 'secondary',
  DEFAULT = 'default',
  SUCCESS = 'success',
  DANGER = 'danger',
  TRANSPARENT_DARK = 'transparent-dark',
  TRANSPARENT_LIGHT = 'transparent-light',
  TRANSPARENT_SECONDARY = 'transparent-secondary',
  PRIMARY = 'primary',
  RECRUITER = 'recruiter',
  TRANSPARENT_RECRUITER = 'transparent-recruiter',
  CUSTOM = 'custom',
  PRIMARY_PINK = 'primary-pink',
}

export enum ButtonSize {
  MINI = 'mini',
  SMALL = 'small',
  LARGE = 'large',
  XLARGE = 'xlarge',
  XXLARGE = 'xxlarge',
}

export enum ButtonGroupPosition {
  FIRST = 'first',
  MIDDLE = 'middle',
  LAST = 'last',
}

@Component({
  selector: 'fl-button',
  template: `
    <!-- internal links will open in current tab by default, and requires to be whitelisted for a new tab -->
    <a
      *ngIf="link && !external"
      class="ButtonElement"
      [routerLink]="link"
      [routerLinkActive]="linkActive || ''"
      [routerLinkActiveOptions]="linkActiveOptions || { exact: false }"
      [queryParams]="queryParams"
      [fragment]="fragment"
      [rel]="allRel"
      [target]="newTab || isNewTabWhitelisted ? '_blank' : '_self'"
      [attr.data-color]="color"
      [attr.data-display]="display"
      [attr.disabled]="attrDisabled || busy ? true : undefined"
      aria-live="assertive"
    >
      <ng-container *ngTemplateOutlet="content"></ng-container>
    </a>
    <!-- external links will open in a new tab by default, and requires to be whitelisted for the current tab -->
    <a
      *ngIf="link && external"
      class="ButtonElement"
      [href]="link"
      [rel]="allRel"
      [target]="newTab === false || isSameTabWhitelisted ? '_self' : '_blank'"
      [attr.data-color]="color"
      [attr.data-display]="display"
      [attr.disabled]="attrDisabled || busy ? true : undefined"
      aria-live="assertive"
    >
      <ng-container *ngTemplateOutlet="content"></ng-container>
    </a>

    <button
      *ngIf="!link"
      class="ButtonElement"
      [type]="buttonType"
      [attr.data-color]="color"
      [attr.data-display]="display"
      [attr.disabled]="attrDisabled || busy ? true : undefined"
      aria-live="assertive"
    >
      <ng-container *ngTemplateOutlet="content"></ng-container>
    </button>

    <ng-template #content>
      <ng-content></ng-content>
      <fl-spinner
        *ngIf="busy"
        class="LoadingSpinner"
        [size]="SpinnerSize.SMALL"
        [color]="spinnerColor"
      ></fl-spinner>
    </ng-template>
  `,
  styleUrls: ['./button.component.scss'],
})
export class ButtonComponent implements OnChanges {
  SpinnerColor = SpinnerColor;
  SpinnerSize = SpinnerSize;

  spinnerColor: SpinnerColor;

  @Input() buttonGroupPosition?: ButtonGroupPosition;
  /** Url used for button link case */
  @Input() link?: string;
  @Input() fragment?: string;
  /**
   * Whether to open the link in a new tab
   *
   * FIXME: Deprecated input.
   * This will be removed when all usage has been cleaned up in T154850
   */
  @Input() newTab?: boolean;
  @Input() queryParams?: { [k: string]: any };
  @Input() linkActive?: string[] | string;
  @Input() linkActiveOptions?: { exact: boolean };

  @HostBinding('class.ButtonGroupFirst')
  @Input()
  buttonGroupFirst = false;
  @HostBinding('class.ButtonGroupMiddle')
  @Input()
  buttonGroupMiddle = false;
  @HostBinding('class.ButtonGroupLast')
  @Input()
  buttonGroupLast = false;
  @HostBinding('attr.data-busy')
  @Input()
  busy?: boolean;
  @HostBinding('attr.data-color')
  @Input()
  color: ButtonColor;
  @HostBinding('attr.data-display')
  @Input()
  display: 'block' | 'inline' | 'flex' = 'inline';
  @HostBinding('attr.data-size')
  @Input()
  size: ButtonSize;
  @HostBinding('attr.data-size-tablet')
  @Input()
  sizeTablet?: ButtonSize;
  @HostBinding('attr.data-size-desktop')
  @Input()
  sizeDesktop?: ButtonSize;
  @Input() buttonType: 'submit' | 'button' = 'button';
  @Input()
  set submit(value: boolean) {
    this.buttonType = value ? 'submit' : 'button';
  }

  @HostBinding('attr.disabled') attrDisabled: true | undefined;

  @Input() rel = '';

  @Input()
  set disabled(value: boolean) {
    this.attrDisabled = value ? true : undefined;
  }
  get disabled() {
    return !!this.attrDisabled;
  }

  allRel = '';
  external = false;
  isNewTabWhitelisted = false;
  isSameTabWhitelisted = false;

  constructor(private location: Location) {}

  isExternalLink(link?: string) {
    // Check if the link is http(s) or protocol relative.
    return !!link && (link.startsWith('http') || link.startsWith('//'));
  }

  ngOnChanges(changes: SimpleChanges) {
    this.spinnerColor =
      this.color === ButtonColor.DEFAULT || ButtonColor.TRANSPARENT_DARK
        ? SpinnerColor.GRAY
        : SpinnerColor.LIGHT;

    if (this.buttonGroupPosition) {
      this.buttonGroupFirst =
        this.buttonGroupPosition === ButtonGroupPosition.FIRST;
      this.buttonGroupMiddle =
        this.buttonGroupPosition === ButtonGroupPosition.MIDDLE;
      this.buttonGroupLast =
        this.buttonGroupPosition === ButtonGroupPosition.LAST;
    }

    if ('link' in changes) {
      // Check if the link is http(s) or protocol relative.
      this.external = this.isExternalLink(this.link);

      if (this.external) {
        this.isSameTabWhitelisted = this.isWhitelisted();
      }

      if (!this.external) {
        this.isNewTabWhitelisted = this.isWhitelisted();
      }
    }

    if ('link' in changes || 'rel' in changes) {
      // Add noopener to rel if external link, due to security exploits
      // Ref: https://mathiasbynens.github.io/rel-noopener/
      if (this.isExternalLink(this.link) && !this.rel.includes('noopener')) {
        this.allRel = `${this.rel} noopener`;
      } else {
        this.allRel = this.rel;
      }
    }
  }

  private isWhitelisted() {
    if (!this.link) {
      return false;
    }

    return isLinkWhitelisted(
      {
        source: this.location.pathname,
        destination: this.link,
      },
      this.external,
    );
  }
}
